import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined, EditOutlined,
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    FileDoneOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Col, DatePicker, Empty, Form, Input, Modal, Popconfirm, Row, Select, Space, Table, Tag } from 'antd';
import {
    ArcElement,
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    ChartOptions,
    Legend,
    LineController,
    LineElement,
    LinearScale,
    Plugin,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Bar, Chart } from 'react-chartjs-2';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { layTenTienDoKPI, trangThaiKPI } from '../../constants';
import { dateCommon } from '../../helpers/common';
import { LoiViPhamCaNhan } from '../../models/kpi/KPICaNhan';
import { KPIPhongBanResponse, TheoDoiCongViec } from '../../models/kpi/KPIPhongBan';
import { anGiaoViecKPI, suaGiaoViecKPI, thongKeKPICaNhan, xoaGiaoViecKPI } from '../../services/kpi';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { tienDoColor } from './kpiHelper';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarController,
    BarElement,
    LineController,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

// ========================
// DATA LABEL PLUGIN
// ========================
const dataLabelPlugin: Plugin<'bar'> = {
    id: 'customDataLabels',
    afterDatasetsDraw(chart) {
        const { ctx, chartArea } = chart;
        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            if (!meta.visible) return;
            meta.data.forEach((element: any, index) => {
                const value = dataset.data[index] as number;
                if (value === null || value === undefined) return;
                const isLine = (dataset as any).type === 'line';
                ctx.save();
                ctx.font = 'bold 11px Arial';
                ctx.textAlign = 'center';
                if (isLine) {
                    ctx.fillStyle = '#203864';
                    let y = element.y - 10;
                    if (value >= 95) y = element.y + 18;
                    y = Math.max(chartArea.top + 12, y);
                    ctx.fillText(`${value}%`, element.x, y);
                } else {
                    ctx.fillStyle = (dataset.backgroundColor as string) || '#333';
                    let y = element.y - 6;
                    y = Math.max(chartArea.top + 12, y);
                    ctx.fillText(`${value}`, element.x, y);
                }
                ctx.restore();
            });
        });
    },
};

// ========================
// HELPERS
// ========================
const formatThangNam = (raw: string | Date) => {
    const date = new Date(raw as any);
    if (!isNaN(date.getTime())) {
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${mm}/${yyyy}`;
    }
    return String(raw || '');
};

const rankColor = (xl: string) => {
    const x = (xl || '').toUpperCase();
    if (x === 'A+') return '#facc15';
    if (x === 'A') return '#22c55e';
    if (x === 'B') return '#f97316';
    if (x === 'C') return '#ef4444';
    return '#9ca3af';
};

const dateFormat = ["DD/MM/YYYY", "DDMMYYYY"];
// ========================
// CARD TỔNG QUAN
// ========================
const SummaryCard = ({
    title,
    value,
    icon,
    color,
    trend,
}: {
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    trend?: { up: boolean; text: string };
}) => (
    <div
        style={{
            background: '#fff',
            border: '1px solid #f0f0f0',
            borderRadius: 12,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            height: '100%',
        }}
    >
        <div
            style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: `${color}1a`,
                color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
            }}
        >
            {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>
                {title}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>{value}</div>
            {trend && (
                <div style={{ fontSize: 12, color: trend.up ? '#16a34a' : '#dc2626', marginTop: 2 }}>
                    {trend.up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {trend.text}
                </div>
            )}
        </div>
    </div>
);

// ========================
// COMPONENT
// ========================
const ThongKeKPICaNhan = ({
    idKPI, trangThai, thangNam, user, setIsReloadPage
}: {
    idKPI: number;
    trangThai: number;
    thangNam: string;
    user: any;
    setIsReloadPage: ()=> void
}) => {
    const dispatch = useDispatch();
    const [data, setData] = useState<Array<KPIPhongBanResponse>>([]);
    const [giaoViecs, setGiaoViecs] = useState<Array<TheoDoiCongViec>>([]);
    const [filterThang, setFilterThang] = useState<string | undefined>(undefined);
    // công việc đang được lãnh đạo giao việc mở modal để sửa lại thông tin
    const [giaoViecDangSua, setGiaoViecDangSua] = useState<any>(null);
    const [dangLuuGiaoViec, setDangLuuGiaoViec] = useState<boolean>(false);
    // chuỗi công việc đang được hỏi xác nhận xóa
    const [idNhomXoaGiaoViec, setIdNhomXoaGiaoViec] = useState<number>(0);
    const [formSuaGiaoViec] = Form.useForm();

    useEffect(() => {
        if (!giaoViecDangSua) {
            formSuaGiaoViec.resetFields();
            return;
        }
        formSuaGiaoViec.setFieldsValue({
            tenCongViec: giaoViecDangSua.tenCongViec,
            thoiGianGiao: giaoViecDangSua.thoiGianGiao ? dayjs(giaoViecDangSua.thoiGianGiao) : null,
            thoiGianYeuCauHoanThanh: giaoViecDangSua.thoiGianYeuCauHoanThanh ? dayjs(giaoViecDangSua.thoiGianYeuCauHoanThanh) : null,
            choSuaThoiGianHT: !!giaoViecDangSua.choSuaThoiGianHT,
            ghiChu: giaoViecDangSua.ghiChu,
        });
    }, [giaoViecDangSua]);

    const handleAnGiaoViec = async (row: any) => {
        const idChiTiets = theoDoiGiaoViecRows.filter((r) => r.idNhom == row.idNhom).map((r) => r.id);
        if (idChiTiets.length === 0) return;
        try {
            dispatch(showLoadingScreen());
            await anGiaoViecKPI(idChiTiets);
            dispatch(hideLoadingScreen());
            toast.success("Đã ẩn công việc khỏi thống kê");
            await layDuLieu(idKPI);
        } catch (ex) {
            dispatch(hideLoadingScreen());
            toast.error("Ẩn công việc khỏi thống kê không thành công. Vui lòng thử lại");
        }
    }
    const moModalSuaGiaoViec = (row: any) => {
        setGiaoViecDangSua(row);
    }
    // lãnh đạo đã giao việc sửa lại thông tin của chính bản ghi công việc đang xem
    const handleSuaGiaoViec = async (values: any) => {
        if (!giaoViecDangSua) return;
        setDangLuuGiaoViec(true);
        dispatch(showLoadingScreen());
        try {
            await suaGiaoViecKPI({
                idChiTiets: [giaoViecDangSua.id],
                tenCongViec: values.tenCongViec,
                thoiGianGiao: values.thoiGianGiao ? values.thoiGianGiao.toDate() : null,
                thoiGianYeuCauHoanThanh: values.thoiGianYeuCauHoanThanh ? values.thoiGianYeuCauHoanThanh.toDate() : null,
                choSuaThoiGianHT: true,
                ghiChu: values.ghiChu,
            });
            setGiaoViecDangSua(null);
            toast.success("Sửa công việc thành công");
            await layDuLieu(idKPI);
            setIsReloadPage(true);
        } catch (ex: any) {
            toast.error(ex?.message || "Sửa công việc không thành công. Vui lòng thử lại");
        }
        setDangLuuGiaoViec(false);
        dispatch(hideLoadingScreen());
    }

    // xóa cả chuỗi bản ghi của công việc (tất cả các tháng kế thừa nhau) khỏi KPI nhân viên
    const handleXoaGiaoViec = async (row: any) => {
        const idChiTiets = theoDoiGiaoViecRows.filter((r) => r.idNhom == row.idNhom).map((r) => r.id);
        if (idChiTiets.length === 0) return;
        dispatch(showLoadingScreen());
        try {
            await xoaGiaoViecKPI(idChiTiets);
            toast.success("Xóa công việc thành công");
            await layDuLieu(idKPI);
            setIsReloadPage(true);
        } catch (ex: any) {
            toast.error(ex?.message || "Xóa công việc không thành công. Vui lòng thử lại");
        }
        dispatch(hideLoadingScreen());
    }
    const layDuLieu = async (id: number) => {
        try {
            dispatch(showLoadingScreen());
            const result = await thongKeKPICaNhan(id);
            setData(Array.isArray(result?.data) ? result.data : []);
            setGiaoViecs(Array.isArray(result?.theoDoiGiaoViecs) ? result.theoDoiGiaoViecs : []);
        } catch (e) {
            setData([]);
            setGiaoViecs([]);
            toast.error('Không thể lấy thông tin');
        }
        dispatch(hideLoadingScreen());
    };

    useEffect(() => {
        if (idKPI > 0) layDuLieu(idKPI);
    }, [idKPI]);

    // ==========================================
    // TỔNG HỢP
    // ==========================================
    const tongQuan = useMemo(() => {
        const tongCV = data.reduce((s, d) => s + (d.soCongViec || 0), 0);
        const tongHT = data.reduce((s, d) => s + (d.truoc || 0) + (d.dung || 0), 0);
        const tongDTH = data.reduce((s, d) => s + (d.dangThucHien || 0), 0);
        const tongTre = data.reduce((s, d) => s + (d.tre || 0) + (d.loi || 0), 0);
        const tyLe = tongCV > 0 ? Math.round((tongHT / tongCV) * 100) : 0;
        return { tongCV, tongHT, tongDTH, tongTre, tyLe };
    }, [data]);

    // ==========================================
    // BIỂU ĐỒ CÔNG VIỆC THEO THÁNG (CỘT + ĐƯỜNG)
    // ==========================================
    const labels = data.map((d) => formatThangNam(d.thangNam));
    const tongSoCongViec = data.map((d) => d.soCongViec || 0);
    const hoanThanh = data.map((d) => (d.truoc || 0) + (d.dung || 0));
    const dangThucHienArr = data.map((d) => d.dangThucHien || 0);
    const khongHoanThanh = data.map((d) => (d.tre || 0) + (d.loi || 0));
    const tyLeHoanThanh = data.map((d) => {
        const tong = d.soCongViec || 0;
        const ht = (d.truoc || 0) + (d.dung || 0);
        return tong > 0 ? Math.round((ht / tong) * 100) : 0;
    });

    const chartData: any = {
        labels,
        datasets: [
            { type: 'bar', label: 'Tổng số công việc', data: tongSoCongViec, backgroundColor: '#4472C4', borderRadius: 3, yAxisID: 'yCongViec' },
            { type: 'bar', label: 'Hoàn thành', data: hoanThanh, backgroundColor: '#22c55e', borderRadius: 3, yAxisID: 'yCongViec' },
            { type: 'bar', label: 'Đang thực hiện', data: dangThucHienArr, backgroundColor: '#FFC000', borderRadius: 3, yAxisID: 'yCongViec' },
            { type: 'bar', label: 'Không hoàn thành', data: khongHoanThanh, backgroundColor: '#FF0000', borderRadius: 3, yAxisID: 'yCongViec' },
            {
                type: 'line',
                label: 'Tỷ lệ hoàn thành (%)',
                data: tyLeHoanThanh,
                borderColor: '#203864',
                backgroundColor: '#203864',
                borderWidth: 2,
                pointRadius: 5,
                tension: 0,
                yAxisID: 'yTyLe',
            },
        ],
    };

    const maxCongViec = Math.max(...tongSoCongViec, 10);
    const roundedMax = Math.ceil((maxCongViec * 1.4) / 10) * 10;

    const options: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 30 } },
        datasets: {
            bar: { barThickness: 18, maxBarThickness: 18, categoryPercentage: 0.6, barPercentage: 0.5 },
        },
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top', align: 'start' },
            title: {
                display: false,
                text: 'TÌNH HÌNH KẾT QUẢ CÔNG VIỆC THEO THÁNG',
                align: 'center',
                color: '#203864',
                position: 'bottom',
            },
        },
        scales: {
            yCongViec: { beginAtZero: true, max: roundedMax, title: { display: true, text: 'Công việc' } },
            yTyLe: {
                position: 'right',
                beginAtZero: true,
                max: 100,
                grid: { drawOnChartArea: false },
                ticks: { callback: (val) => `${val}%` },
            },
        },
    };

    // ==========================================
    // BIỂU ĐỒ ĐIỂM KPI THEO THÁNG
    // ==========================================
    const avgKPIPlugin: Plugin<'bar'> = {
        id: 'avgKPIPlugin',
        afterDraw: (chart) => {
            const { ctx, chartArea } = chart;

            if (!chartArea) return;

            const values = chart.data.datasets[0].data as number[];
            const avg =
                values.reduce((a, b) => a + b, 0) / (values.length || 1);

            ctx.save();

            ctx.font = 'bold 12px Arial';
            ctx.fillStyle = '#203864';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';

            ctx.fillText(
                `Trung bình: ${avg.toFixed(1)}`,
                chartArea.right - 5,
                chartArea.top + 25
            );

            ctx.restore();
        },
    };
    const xepLoaiPlugin: Plugin<'bar'> = {
        id: 'xepLoaiPlugin',
        afterDatasetsDraw: (chart) => {
            const { ctx, chartArea } = chart;

            const meta = chart.getDatasetMeta(0);

            meta.data.forEach((bar: any, index: number) => {
                const xepLoai = data[index]?.xepLoai || '';

                ctx.save();
                ctx.fillStyle = '#203864';
                ctx.font = '12px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';

                // 👇 vẽ dưới cột (sát trục X)
                ctx.fillText(
                    xepLoai,
                    bar.x,
                    chartArea.bottom - 18
                );

                ctx.restore();
            });
        },
    };
    const diemKPI = data.map((d) => d.diemDanhGia);
    const diemColors = data.map((d) => rankColor(d.xepLoai));

    const scoreData: any = {
        labels,
        datasets: [
            {
                label: 'Điểm KPI',
                data: diemKPI,
                backgroundColor: diemColors,
                borderRadius: 6,
                barThickness: 30,
            },
        ],
    };
    const scoreOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                bottom: 30, // 👈 bắt buộc để không bị cắt chữ
            },
        },
        plugins: {
            legend: { display: false },
            title: {
                display: false,
                text: 'ĐIỂM KPI THEO THÁNG',
                color: '#203864',
                position: 'bottom',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const index = context.dataIndex;
                        const diem = data[index]?.diemDanhGia;
                        const xepLoai = data[index]?.xepLoai;

                        return [
                            `Điểm: ${diem}`,
                            `Xếp loại: ${xepLoai}`,
                        ];
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                ticks: { stepSize: 50 },
            },
        },
    };
    // ==========================================
    // BẢNG LỖI VI PHẠM
    // ==========================================
    const monthOptions = useMemo(() => {
        const opts = data.map((d) => ({ label: formatThangNam(d.thangNam), value: formatThangNam(d.thangNam) }));
        return [...opts];
    }, [data]);

    const violations = useMemo(() => {
        const all: Array<LoiViPhamCaNhan & { thang: string }> = [];
        data.forEach((d) => {
            const thang = formatThangNam(d.thangNam);
            (d.loiViPhams || []).forEach((v) => all.push({ ...v, thang }));
        });
        if (filterThang == undefined) return all;
        return all.filter((v) => v.thang === filterThang);
    }, [data, filterThang]);

    const tongDiemTru = violations.reduce((s, v) => s + (v.diemTru || 0), 0);

    // ==========================================
    // BẢNG THEO DÕI VÒNG ĐỜI CÔNG VIỆC (bắt đầu -> hoàn thành)
    // ==========================================
    const theoDoiColumns = [
        {
            title: 'Tên công việc',
            dataIndex: 'tenCongViec',
            key: 'tenCongViec',
            onCell: (row: any) => ({ rowSpan: row.rowSpan }),
        },
        {
            title: 'Tháng',
            dataIndex: 'thang',
            key: 'thang',
            width: 90,
            align: 'center' as const,
            render: (v: string) => <b>{v}</b>,
        },
        {
            title: 'Tiến độ',
            dataIndex: 'tienDo',
            key: 'tienDo',
            width: 100,
            align: 'center' as const,
            render: (v: number) => (
                <Tag color={tienDoColor(v)} style={{ color: '#fff', fontWeight: 600, margin: 0 }}>
                    {layTenTienDoKPI(v)}
                </Tag>
            ),
        },
    ];

    const theoDoiRows = useMemo(() => {
        const all: Array<{ idNhom: number; tenCongViec: string; thang: string; thangNam: string; tienDo: number }> = [];
        data.forEach((d) => {
            (d.theoDoiCongViecs || []).forEach((cv: any) => {
                all.push({
                    idNhom: cv.idNhom,
                    tenCongViec: cv.tenCongViec || '-',
                    thang: formatThangNam(cv.thangNam),
                    thangNam: cv.thangNam,
                    tienDo: cv.tienDo,
                });
            });
        });
        // sắp xếp theo nhóm công việc, rồi theo thời gian để thấy vòng đời từ lúc bắt đầu đến khi hoàn thành
        all.sort((a, b) => {
            if (a.idNhom !== b.idNhom) return a.idNhom - b.idNhom;
            return new Date(a.thangNam).getTime() - new Date(b.thangNam).getTime();
        });
        // tính rowSpan để gộp ô tên công việc theo nhóm
        const countByNhom: Record<number, number> = {};
        all.forEach((r) => { countByNhom[r.idNhom] = (countByNhom[r.idNhom] || 0) + 1; });
        const seen: Record<number, boolean> = {};
        return all.map((r, i) => {
            const isFirst = !seen[r.idNhom];
            seen[r.idNhom] = true;
            return { ...r, key: i, rowSpan: isFirst ? countByNhom[r.idNhom] : 0 };
        });
    }, [data]);
    // ==========================================
    // BẢNG THEO DÕI GIAO VIỆC (bắt đầu -> hoàn thành)
    // ==========================================
    const theoDoiGiaoViecRows = useMemo(() => {
        const all: Array<{
            id: number; idNhom: number; tenCongViec: string; thang: string; thangNam: string; thoiGian: string; tienDo: number, tenNVGiao: string,
            ghiChu: string, idNVGiao: number, trangThai: number, thoiGianGiao: string, thoiGianYeuCauHoanThanh: string,
        }> = [];

        (giaoViecs || []).forEach((cv: any) => {
            all.push({
                id: cv.id,
                idNhom: cv.idNhom,
                tenCongViec: cv.tenCongViec || '-',
                thang: formatThangNam(cv.thangNam),
                thoiGian: `${dateCommon.formatDate(cv.thoiGianGiao, "dd/MM/yyyy")}-${dateCommon.formatDate(cv.thoiGianYeuCauHoanThanh, "dd/MM/yyyy")}`,
                thangNam: cv.thangNam,
                tienDo: cv.tienDo,
                tenNVGiao: cv.tenNVGiao,
                ghiChu: cv.ghiChu,
                idNVGiao: cv.idNVGiao,
                trangThai: cv.trangThai,
                thoiGianGiao: cv.thoiGianGiao,
                thoiGianYeuCauHoanThanh: cv.thoiGianYeuCauHoanThanh,
            });
        });
        // sắp xếp theo nhóm công việc, rồi theo thời gian để thấy vòng đời từ lúc bắt đầu đến khi hoàn thành
        all.sort((a, b) => {
            if (a.idNhom !== b.idNhom) return a.idNhom - b.idNhom;
            return new Date(a.thangNam).getTime() - new Date(b.thangNam).getTime();
        });
        // tính rowSpan để gộp ô tên công việc theo nhóm
        const countByNhom: Record<number, number> = {};
        all.forEach((r) => { countByNhom[r.idNhom] = (countByNhom[r.idNhom] || 0) + 1; });
        const seen: Record<number, boolean> = {};
        return all.map((r, i) => {
            const isFirst = !seen[r.idNhom];
            seen[r.idNhom] = true;
            return { ...r, key: i, rowSpan: isFirst ? countByNhom[r.idNhom] : 0, soThang: countByNhom[r.idNhom] };
        });
    }, [giaoViecs]);
    const coThaoTacGiaoViec = useMemo(() => (giaoViecs || []).some((cv: any) => cv.idNVGiao == user.idNhanVien), [giaoViecs, user.idNhanVien]) ;
    const theoDoiGiaoViecColumns = [
        {
            title: 'Lãnh đạo giao',
            dataIndex: 'tenNVGiao',
            key: 'tenNVGiao',
            onCell: (row: any) => ({ rowSpan: row.rowSpan }),
        },
        {
            title: 'Tên công việc',
            dataIndex: 'tenCongViec',
            key: 'tenCongViec',
            onCell: (row: any) => ({ rowSpan: row.rowSpan }),
        },
        {
            title: 'Thời gian',
            dataIndex: 'thoiGian',
            key: 'thoiGian',
            width: 190,
            align: 'center' as const,
            render: (v: string) => <b>{v}</b>,
        },
        {
            title: 'Tiến độ',
            dataIndex: 'tienDo',
            key: 'tienDo',
            width: 100,
            align: 'center' as const,
            render: (v: number) => (
                <Tag color={tienDoColor(v)} style={{ color: '#fff', fontWeight: 600, margin: 0 }}>
                    {layTenTienDoKPI(v)}
                </Tag>
            ),
        },
        {
            title: 'Ghi chú',
            dataIndex: 'ghiChu',
            key: 'ghiChu',
            render: (v: string) => <span>{v}</span>,
        }, 
        {
            title: '',
            key: 'thaoTacGiaoViec',
            width: 95,
            align: 'center' as const,
            render: (_: any, row: any) => (
                <Space size={4}>
                    {row.idNVGiao == user.idNhanVien ?
                        ((row.soThang == 1 && row.trangThai < trangThaiKPI.trinhLanhDaoPhong) ?
                            < Button
                                className="hrm-btn hrm-btn-success"
                                size="small" title="Chỉnh sửa"
                                icon={<EditOutlined />}
                                onClick={() => moModalSuaGiaoViec(row)}
                            >
                            </Button> : <Popconfirm
                                title="Kết thúc theo dõi công việc"
                                description="Toàn bộ bản ghi của công việc này sẽ không hiển thị tại bảng theo dõi công việc được giao. Bạn có chắc chắn?"
                                okText="Đồng ý"
                                cancelText="Hủy"
                                onConfirm={() => handleAnGiaoViec(row)}
                                overlayClassName="hrm-popconfirm"
                                // tô sáng cả chuỗi công việc sắp bị ẩn trong lúc hỏi xác nhận
                                onOpenChange={(mo) => setIdNhomXoaGiaoViec(mo ? row.idNhom : 0)}
                            >
                                <Button danger size="small" className="hrm-btn-warning" title="Kết thúc theo dõi" icon={<EyeInvisibleOutlined />}
                                >
                                </Button>
                            </Popconfirm>) : null}
                    {
                        row.idNVGiao == user.idNhanVien && <Popconfirm
                            title="Xóa công việc đã giao"
                            description="Toàn bộ bản ghi của công việc này sẽ bị xóa khỏi KPI cá nhân. Bạn có chắc chắn?"
                            okText="Xóa"
                            cancelText="Hủy"
                            onConfirm={() => handleXoaGiaoViec(row)}
                            overlayClassName="hrm-popconfirm"
                            // tô sáng cả chuỗi công việc sắp bị xóa trong lúc hỏi xác nhận
                            onOpenChange={(mo) => setIdNhomXoaGiaoViec(mo ? row.idNhom : 0)}
                        >
                            <Button danger size="small" className="hrm-btn-delete" title="Xóa công việc" icon={<DeleteOutlined />}
                            >
                            </Button>
                        </Popconfirm>}
                </Space>
            ),
        },
    ].filter((c) => c.key !== 'thaoTacGiaoViec' || coThaoTacGiaoViec);
    const violationColumns = [
        { title: 'Tháng', dataIndex: 'thang', key: 'thang', width: 68, render: (v: string) => <b>{v}</b> },
        { title: 'Nội dung vi phạm', dataIndex: 'noiDung', key: 'noiDung' },
        {
            title: 'Điểm trừ',
            dataIndex: 'diemTru',
            key: 'diemTru',
            align: 'center' as const,
            width: 82,
            render: (v: number | null) =>
                v ? <Tag color="#ef4444" style={{ color: '#fff', fontWeight: 700 }}>-{Math.abs(v)}</Tag> : '-',
        },
        { title: 'Ghi chú', dataIndex: 'ghiChu', key: 'ghiChu', width: 200 },
    ];

    return (
        <section>
            <div className="card card-body" style={{ padding: 16, backgroundColor: '#fff', borderRadius: 8 }}>
                {data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                        <Empty description="Không có dữ liệu" />
                    </div>
                ) : (
                    <>
                        <Row gutter={[24, 24]}>
                            <Col span={24} style={{ marginBottom: -15 }}>
                                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#203864', textTransform: 'uppercase', marginBottom: 16 }}>
                                    ĐIỂM KPI THEO THÁNG
                                </h4>

                                <div style={{ height: 200, background: '#fff', borderRadius: 12 }}>
                                    <Bar
                                        data={scoreData}
                                        options={scoreOptions}
                                        plugins={[xepLoaiPlugin, avgKPIPlugin]}
                                    />
                                </div>
                            </Col>
                            <Col span={6}>
                                <SummaryCard
                                    title="Tổng công việc"
                                    value={tongQuan.tongCV}
                                    icon={<FileDoneOutlined />}
                                    color="#4472C4"
                                    trend={{ up: true, text: `Tỷ lệ HT ${tongQuan.tyLe}%` }}
                                />
                            </Col>
                            <Col span={6}>
                                <SummaryCard
                                    title="Đã hoàn thành"
                                    value={tongQuan.tongHT}
                                    icon={<CheckCircleOutlined />}
                                    color="#22c55e"
                                />
                            </Col>
                            <Col span={6}>
                                <SummaryCard
                                    title="Đang thực hiện"
                                    value={tongQuan.tongDTH}
                                    icon={<ClockCircleOutlined />}
                                    color="#f59e0b"
                                />
                            </Col>
                            <Col span={6}>
                                <SummaryCard
                                    title="Không hoàn thành"
                                    value={tongQuan.tongTre}
                                    icon={<ExclamationCircleOutlined />}
                                    color="#ef4444"
                                />
                            </Col>
                            {/* BIỂU ĐỒ CỘT + ĐƯỜNG */}
                            <Col span={24} style={{ marginTop: 25 }}>
                                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#203864', textTransform: 'uppercase', marginBottom: -5 }}>
                                    TÌNH HÌNH KẾT QUẢ CÔNG VIỆC THEO THÁNG
                                </h4>
                                <div style={{ height: 340 }}>
                                    <Chart
                                        type="bar"
                                        data={chartData}
                                        options={options as any}
                                        plugins={[dataLabelPlugin as any]}
                                    />
                                </div>
                            </Col>

                            {/* BẢNG THEO DÕI VÒNG ĐỜI CÔNG VIỆC */}
                            <Col span={12} style={{ marginTop: 20 }}>
                                <Row align="middle" justify="space-between" style={{ marginBottom: "9px" }}>
                                    <Col span={24}>
                                        <h4
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 700,
                                                color: '#203864',
                                                textTransform: 'uppercase',
                                                marginBottom: 0,
                                                marginRight: "10px",
                                                display: 'inline-block',
                                            }}
                                        >
                                            THEO DÕI CÔNG VIỆC CHƯA HOÀN THÀNH
                                        </h4>
                                        <Space >
                                            <Tag
                                                color="#ef4444"
                                                style={{
                                                    color: '#fff',
                                                    margin: 0,
                                                    padding: '2px 8px',
                                                    lineHeight: '1.5',
                                                    fontSize: '12px',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {new Set(theoDoiRows.map((r) => r.idNhom)).size} CÔNG VIỆC
                                            </Tag>
                                        </Space>
                                    </Col>
                                </Row>
                                <Table
                                    size="small"
                                    columns={theoDoiColumns}
                                    dataSource={theoDoiRows}
                                    pagination={false}
                                    scroll={{ y: 400 }}
                                    bordered
                                    locale={{ emptyText: 'Không có công việc cần theo dõi' }}
                                />
                            </Col>

                            {/* BẢNG LỖI VI PHẠM */}
                            <Col span={12} style={{ marginTop: 20 }}>
                                <Row align="middle" justify="space-between" style={{ marginBottom: "9px" }}>
                                    <Col span={18}>
                                        <h4
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 700,
                                                color: '#203864',
                                                textTransform: 'uppercase',
                                                marginBottom: 0,
                                                marginRight: "10px",
                                                display: 'inline-block',
                                            }}
                                        >
                                            LỖI VI PHẠM
                                        </h4>
                                        <Space >
                                            <Tag
                                                color="#ef4444"
                                                style={{
                                                    color: '#fff',
                                                    margin: 0,
                                                    padding: '2px 8px',
                                                    lineHeight: '1.5',
                                                    fontSize: '12px',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {violations.length} LỖI
                                            </Tag>

                                            <Tag
                                                color="#dc2626"
                                                style={{
                                                    color: '#fff',
                                                    margin: 0,
                                                    padding: '2px 8px',
                                                    lineHeight: '1.5',
                                                    fontSize: '12px',
                                                    fontWeight: 600
                                                }}
                                            >
                                                -{Math.abs(tongDiemTru)} ĐIỂM
                                            </Tag>
                                        </Space>
                                    </Col>
                                    <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Select
                                            value={filterThang}
                                            onChange={setFilterThang}
                                            options={monthOptions}
                                            style={{ minWidth: 200 }}
                                            size="middle"
                                            allowClear
                                            placeholder="Tất cả các tháng"
                                        />
                                    </Col>
                                </Row>
                                <Table
                                    size="small"
                                    columns={violationColumns}
                                    dataSource={violations.map((v, i) => ({ ...v, key: i }))}
                                    pagination={false}
                                    bordered
                                    locale={{ emptyText: 'Không có lỗi vi phạm trong khoảng thời gian này' }}
                                />
                            </Col>

                            {/* BẢNG THEO DÕI CÔNG VIỆC ĐƯỢC GIAO */}
                            <Col span={24} style={{ marginTop: 20 }}>
                                <Row align="middle" justify="space-between" style={{ marginBottom: "9px" }}>
                                    <Col span={24}>
                                        <h4
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 700,
                                                color: '#203864',
                                                textTransform: 'uppercase',
                                                marginBottom: 0,
                                                marginRight: "10px",
                                                display: 'inline-block',
                                            }}
                                        >
                                            THEO DÕI CÔNG VIỆC ĐƯỢC GIAO
                                        </h4>
                                        <Space >
                                            <Tag
                                                color="#ef4444"
                                                style={{
                                                    color: '#fff',
                                                    margin: 0,
                                                    padding: '2px 8px',
                                                    lineHeight: '1.5',
                                                    fontSize: '12px',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {new Set(theoDoiGiaoViecRows.map((r) => r.idNhom)).size} CÔNG VIỆC
                                            </Tag>
                                        </Space>
                                    </Col>
                                </Row>
                                <Table
                                    size="small"
                                    columns={theoDoiGiaoViecColumns}
                                    dataSource={theoDoiGiaoViecRows}
                                    pagination={false}
                                    scroll={{ y: 400 }}
                                    bordered
                                    rowClassName={(record: any) => ((!!idNhomXoaGiaoViec && record.idNhom === idNhomXoaGiaoViec)
                                                    || (!!giaoViecDangSua && record.id === giaoViecDangSua.id)) ? "selected-row row-key-" : ""}
                                    locale={{ emptyText: 'Không có công việc được giao' }}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </div>
            <Modal
                title="Sửa công việc đã báo cáo"
                open={!!giaoViecDangSua}
                onOk={() => formSuaGiaoViec.submit()}
                onCancel={() => setGiaoViecDangSua(null)}
                okText="Lưu"
                cancelText="Hủy"
                confirmLoading={dangLuuGiaoViec}
                maskClosable={false}
                destroyOnClose
                width={640}
            >
                <Form
                    form={formSuaGiaoViec}
                    layout="vertical"
                    preserve={false}
                    onFinish={handleSuaGiaoViec}
                    initialValues={{
                        tenCongViec: giaoViecDangSua?.tenCongViec,
                        thoiGianGiao: giaoViecDangSua?.thoiGianGiao ? dayjs(giaoViecDangSua.thoiGianGiao) : null,
                        thoiGianYeuCauHoanThanh: giaoViecDangSua?.thoiGianYeuCauHoanThanh ? dayjs(giaoViecDangSua.thoiGianYeuCauHoanThanh) : null,
                        choSuaThoiGianHT: !!giaoViecDangSua?.choSuaThoiGianHT,
                        ghiChu: giaoViecDangSua?.ghiChu,
                    }}
                >
                    <Form.Item
                        label="Nội dung công việc"
                        name="tenCongViec"
                        rules={[{ required: true, message: "Tên công việc bắt buộc nhập" }]}
                    >
                        <Input.TextArea rows={2} placeholder="Nhập công việc" />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Thời gian giao"
                                name="thoiGianGiao"
                                rules={[{ required: true, message: "Thời gian giao bắt buộc nhập" }]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    changeOnBlur
                                    allowClear={false}
                                    format={dateFormat}
                                    onChange={(e: any) => {
                                        const hoanThanh = formSuaGiaoViec.getFieldValue("thoiGianYeuCauHoanThanh");
                                        if (e && hoanThanh && e.isAfter(hoanThanh)) {
                                            formSuaGiaoViec.setFieldValue("thoiGianYeuCauHoanThanh", e);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Thời gian yêu cầu hoàn thành"
                                name="thoiGianYeuCauHoanThanh"
                                style={{ marginBottom: 4 }}
                                rules={[
                                    { required: true, message: "Thời gian yêu cầu hoàn thành bắt buộc nhập" },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const giao = getFieldValue("thoiGianGiao");
                                            if (!value || !giao || !value.isBefore(giao, 'day')) return Promise.resolve();
                                            return Promise.reject(new Error("Thời gian yêu cầu hoàn thành không được trước thời gian giao"));
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    changeOnBlur
                                    allowClear={false}
                                    placeholder="Chọn ngày"
                                    format={dateFormat}
                                />
                            </Form.Item>
                            <Form.Item name="choSuaThoiGianHT" valuePropName="checked" hidden>
                                <Checkbox title="Cho sửa thời gian yêu cầu hoàn thành">Cho sửa</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Ghi chú" name="ghiChu" style={{ marginBottom: 0 }}>
                        <Input.TextArea rows={2} placeholder="Nhập ghi chú" />
                    </Form.Item>
                </Form>
            </Modal>
        </section>
    );
};

export default ThongKeKPICaNhan;